import React from 'react';
import TransitionRoot, {getTransitionDuration, TransitionRootProps} from './TransitionRoot';

export type Direction = 'left' | 'right' | 'up' | 'down';

export interface SlideProps extends TransitionRootProps {
  direction?: Direction;
}

export const Slide: React.FC<SlideProps> = ({
  children,
  timeout,
  in: inProp,
  direction = 'right',
  nodeRef: nodeRefProp,
  ...restProps
}) => {
  const [node, setNode] = React.useState<HTMLElement | null>(null);
  const rootRef = React.useRef(null);
  const nodeRef = nodeRefProp || rootRef;

  const transformStyle = React.useMemo(() => {
    const rectDocument = document.body.getBoundingClientRect();

    switch (direction) {
      case 'left':
        return `translateX(-${(node?.offsetLeft || 0) + (node?.offsetWidth || 0)}px)`;
      case 'right':
        return `translateX(${rectDocument.right - (node?.offsetLeft || 0)}px)`;
      case 'up':
        return `translateY(${rectDocument.bottom - (node?.offsetTop || 0)}px)`;
      case 'down':
        return `translateY(-${(node?.offsetTop || 0) + (node?.offsetHeight || 0)}px)`;
    }
  }, [node, direction]);

  const defaultStyle: React.CSSProperties = {
    transition: `transform ${getTransitionDuration(timeout, 'enter')}ms ease-in-out`,
    transform: transformStyle,
  };

  const transitionStyles: {[name: string]: React.CSSProperties} = {
    entering: {
      transitionDuration: getTransitionDuration(timeout, 'enter').toString(),
      transform: transformStyle,
    },
    entered: {
      transitionDuration: getTransitionDuration(timeout, 'enter').toString(),
      transform: 'translate(0)',
    },
    exiting: {
      transitionDuration: getTransitionDuration(timeout, 'exit').toString(),
      transform: transformStyle,
    },
    exited: {
      transitionDuration: getTransitionDuration(timeout, 'exit').toString(),
      transform: transformStyle,
    },
  };

  const getChildrenWithProps = (state) => {
    return React.Children.map(children, (child: React.ReactNode) => {
      if (React.isValidElement(child)) {
        return React.cloneElement<any>(child, {
          ref: nodeRef,
          style: {
            ...defaultStyle,
            ...transitionStyles[state],
            ...child?.props?.style,
          },
        });
      }
      return child;
    });
  };

  const handleEnter = (node: HTMLElement) => {
    setNode(node);
  };

  return (
    <TransitionRoot
      nodeRef={nodeRef}
      timeout={timeout}
      in={inProp}
      appear={inProp}
      onEnter={handleEnter}
      {...restProps}
    >
      {(state) => getChildrenWithProps(state)}
    </TransitionRoot>
  );
};

export default Slide;
